import type { Nullable } from '@accesso/cc-std-lib';
import type { MetaDefinition } from '@angular/platform-browser';
import { PlatformLocation } from '@angular/common';
import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { isNonEmptyString } from '@accesso/cc-std-lib';

import { EnvironmentService } from '../../core/environment/environment.service';

export interface MetaData {
	title: string;
	type?: Nullable<string>;
	description?: Nullable<string>;
	image?: Nullable<string>;
	canonicalUrl?: Nullable<string>;
	keywords?: Nullable<string>;
	author?: Nullable<string>;
}

@Injectable({
	providedIn: 'root',
})
export class MetaDataService {
	private readonly title = inject(Title);
	private readonly meta = inject(Meta);
	private readonly env = inject(EnvironmentService);
	private readonly platformLocation = inject(PlatformLocation);
	private readonly locale = inject(LOCALE_ID);

	hostUrl(): string {
		const { protocol, hostname } = this.platformLocation;
		return `${protocol}//${hostname}`;
	}

	canonicalUrl(): string {
		const { pathname, search } = this.platformLocation;
		return `${this.hostUrl()}${pathname}${search}`;
	}

	setMetaData({ title, description, image, type, canonicalUrl }: MetaData): void {
		const { meta } = this;
		this.title.setTitle(title);
		const tags: MetaDefinition[] = [
			{ name: 'og:title', content: title },
			{ name: 'og:url', content: canonicalUrl ?? this.canonicalUrl() },
			{ name: 'og:locale', content: this.locale },
		];

		if (isNonEmptyString(description)) {
			tags.push(
				{ name: 'description', content: description },
				// Max length for og:description is 300 characters, but the recommendation is 200 to support the largest platforms
				{ name: 'og:description', content: description.slice(0, 200) }
			);
		}

		if (isNonEmptyString(image)) {
			tags.push({ name: 'og:image', content: !image.startsWith('http') ? `https://${this.env.assets}/${image}` : image });
		}

		tags.push({ name: 'og:type', content: isNonEmptyString(type) ? type : 'website' });

		// Remove potentially stale meta tags in case it's one we're not replacing
		['description', 'og:title', 'og:description', 'og:image', 'og:url', 'og:type', 'og:locale'].forEach((name) => meta.removeTag(`name="${name}"`));
		meta.addTags(tags);
	}
}
